import styled from "styled-components";

import { FONT_SIZES, SPACING } from "../../../../../utils/constants";
import { FigureTone } from "./types";

// Figures are small (14px) data text, so positive uses the AA-compliant success *text* token
// (var(--color-success) is a brighter accent that fails AA on the white light-theme surface).
const TONE_COLOR: Record<FigureTone, string> = {
  positive: "var(--color-success-text)",
  negative: "var(--color-error)",
  neutral: "var(--color-text-default)",
};

export const PreviewWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xl}px;
`;

export const PeriodLabel = styled.p`
  font-size: ${FONT_SIZES.sm};
  color: var(--color-text-secondary);
  margin: 0;
`;

/* The report's two conclusions — period balance and net-worth change — carried at
   display scale. Type does the work: no tile chrome, no grid, just the figures
   that answer "how did I do", set apart from the ledger below by a single rule. */
export const Hero = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.xl}px;
  padding-bottom: ${SPACING.lg}px;
  border-bottom: 1px solid var(--color-border);
`;

export const HeroFigure = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  min-width: 160px;
`;

export const HeroLabel = styled.span`
  font-size: ${FONT_SIZES.xs};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-secondary);
`;

export const HeroValue = styled.span<{ $tone?: FigureTone }>`
  font-size: ${FONT_SIZES.display};
  font-weight: 700;
  line-height: 1.1;
  /* Tighten the tracking at display scale so the serif reads as a set figure, not spaced-out body. */
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone = "neutral" }) => TONE_COLOR[$tone]};
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Weight carries the ramp alongside size: 700 hero conclusions, 600 section headings,
   500 ledger figures (the answers), 400 terms and body. Four steps, all loaded weights. */
export const SectionTitle = styled.h3`
  font-size: ${FONT_SIZES.lg};
  font-weight: 600;
  color: var(--color-text-default);
  margin: 0 0 ${SPACING.sm}px 0;
`;

export const SubSection = styled.div`
  & + & {
    margin-top: ${SPACING.lg}px;
  }
`;

export const SubSectionTitle = styled.h4`
  font-size: ${FONT_SIZES.xs};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-secondary);
  margin: 0 0 ${SPACING.xs}px 0;
`;

/* A ledger of aligned label -> figure rows separated by hairlines. Mirrors the
   PDF's lightHorizontalLines tables so the on-screen report and the download read
   as the same document. Modelled as a description list: each row is a term/figure
   pair, which is what a screen reader should hear. */
export const Ledger = styled.dl`
  display: flex;
  flex-direction: column;
  margin: 0;
`;

export const LedgerRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.sm}px 0;

  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

export const LedgerTerm = styled.dt`
  font-size: ${FONT_SIZES.base};
  color: var(--color-text-secondary);
`;

export const LedgerFigure = styled.dd<{ $tone?: FigureTone }>`
  margin: 0;
  font-size: ${FONT_SIZES.base};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone = "neutral" }) => TONE_COLOR[$tone]};
`;

/* The percentage rides quietly beside the amount in the category ledger. */
export const FigureMuted = styled.span`
  color: var(--color-text-secondary);
`;

export const LedgerNote = styled.p`
  font-size: ${FONT_SIZES.sm};
  color: var(--color-text-secondary);
  margin: ${SPACING.sm}px 0 0 0;
`;

/* Seven columns can't fit a phone, so the table scrolls inside its own box rather than the page. */
export const TableScroll = styled.div`
  overflow-x: auto;
`;

/* The multi-column sibling of Ledger: same hairlines and figure weight, so it mirrors the PDF table too. */
export const IncomeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${FONT_SIZES.sm};
  font-variant-numeric: tabular-nums;
`;

export const IncomeHeaderCell = styled.th<{ $numeric?: boolean }>`
  padding: ${SPACING.sm}px ${SPACING.sm}px ${SPACING.sm}px 0;
  font-weight: 500;
  color: var(--color-text-secondary);
  text-align: ${({ $numeric }) => ($numeric ? "right" : "left")};
  white-space: nowrap;
`;

export const IncomeCell = styled.td<{ $numeric?: boolean }>`
  padding: ${SPACING.sm}px ${SPACING.sm}px ${SPACING.sm}px 0;
  border-top: 1px solid var(--color-border);
  text-align: ${({ $numeric }) => ($numeric ? "right" : "left")};
  font-weight: ${({ $numeric }) => ($numeric ? 500 : 400)};
  color: ${({ $numeric }) =>
    $numeric ? "var(--color-text-default)" : "var(--color-text-secondary)"};
  white-space: ${({ $numeric }) => ($numeric ? "nowrap" : "normal")};
`;

export const IncomeTotalRow = styled.tr`
  & > td {
    font-weight: 600;
    color: var(--color-text-default);
  }
`;
